import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./Home.scss";
import { parseDate } from "../utils/date";

function Home() {
    const navigate = useNavigate();
    const location = useLocation();
    const contactRef = useRef(null);
    
    // Array of background image sources with individual text content
    const backgroundImages = [
        {
            src: "/media/bg-images/bg-image1.JPG",
            title: "Welcome to the Sts.Vartanantz Ladies Guild",
            subtitle: "Uniting the Women of Our Church",
            description: "The Ladies' Guild of Sts. Vartanantz Church unites the women of our Church to support and assist the Pastor and Board of Trustees in various endeavors and activities relating to our parish."
        },
        {
            src: "/media/bg-images/bg-image2.JPG",
            title: "Supporting Our Community",
            subtitle: "Faith, Service, and Tradition",
            description: "Our involvement extends to the Sunday School, Nareg Armenian School, the Choir, and Church youth-oriented organizations."
        },
        {
            src: "/media/bg-images/bg-image3.JPG",
            title: "Preserving Armenian Heritage",
            subtitle: "Learning Our Traditions",
            description: "We inspire our members and local parishioners to learn the doctrines and traditions of the Armenian Apostolic Church."
        }
    ];

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [intervalId, setIntervalId] = useState(null);
    const [latestNews, setLatestNews] = useState([]);
    const [loadingNews, setLoadingNews] = useState(true);
    const [showAllNews, setShowAllNews] = useState(false);
    const [loadingAllNews, setLoadingAllNews] = useState(false);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [copiedLocationId, setCopiedLocationId] = useState(null);
    const [selectedNews, setSelectedNews] = useState(null);
    const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);

    // Refs for scroll animations
    const newsRef = useRef(null);
    const eventsRef = useRef(null);
    const [newsVisible, setNewsVisible] = useState(false);
    const [eventsVisible, setEventsVisible] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);

    // Helper functions for events (reused from Calendar component)
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const cleanText = (text) => {
        if (!text) return '';
        // Remove escape sequences and clean up the text
        return text
            .replace(/\\/g, '') // Remove all backslashes
            .replace(/\n/g, ' ') // Replace newlines with spaces
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim();
    };

    const filterDescription = (description) => {
        if (!description) return '';
        const lastModifiedIndex = description.indexOf('LAST-MODIFIED');
        if (lastModifiedIndex !== -1) {
            return description.substring(0, lastModifiedIndex).trim();
        }
        return description;
    };

    const getUpcomingEventsForHome = () => {
        // Events are already filtered and limited by the API
        return upcomingEvents.map(event => ({
            ...event,
            // Ensure date is in YYYY-MM-DD format
            date: event.date ? (event.date.split('T')[0] || event.date) : event.date,
            // Ensure all required fields have defaults and clean escape sequences
            title: cleanText(event.title || 'Untitled Event'),
            time: event.time || 'All Day',
            location: cleanText(event.location || 'TBD'),
            description: cleanText(filterDescription(event.description || '')),
            type: event.type || 'meeting',
            id: event.id || event.title + event.date || Math.random().toString()
        })).filter(event => event.date); // Filter out events without valid dates
    };

    const getEventTypeLabel = (type) => {
        const labels = {
            meeting: 'Monthly Meeting',
            sale: 'Fundraising Sale',
            cultural: 'Cultural Event',
            prayer: 'Prayer Service',
            planning: 'Planning Meeting'
        };
        return labels[type] || type;
    };

    const formatEventDate = (dateString) => {
        try {
            if (!dateString) return { day: '', month: '', weekday: '' };
            const date = parseDate(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return { day: '', month: '', weekday: '' };
            }
            return {
                day: date.getDate(),
                month: monthNames[date.getMonth()].substring(0, 3),
                weekday: date.toLocaleDateString('en-US', { weekday: 'long' })
            };
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return { day: '', month: '', weekday: '' };
        }
    };

    const formatNewsDate = (dateString) => {
        const date = parseDate(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const copyToClipboard = async (text, eventId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedLocationId(eventId);
            setTimeout(() => {
                setCopiedLocationId(null);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const openInGoogleMaps = (location) => {
        if (!location || location === 'TBD') return;
        const encodedLocation = encodeURIComponent(location);
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodedLocation}`, '_blank');
    };

    // Fetch news from API
    useEffect(() => {
        const fetchNews = async () => {
            try {
                setLoadingNews(true);
                const response = await axios.get('/api/news/?limit=3');
                setLatestNews(response.data);
                setLoadingNews(false);
            } catch (error) {
                console.error('Error fetching news:', error);
                setLoadingNews(false);
                // Keep empty array on error
            }
        };

        fetchNews();
    }, []);

    // Function to load all news
    const loadAllNews = async () => {
        try {
            setLoadingAllNews(true);
            const response = await axios.get('/api/news/');
            setLatestNews(response.data);
            setShowAllNews(true);
            setLoadingAllNews(false);
        } catch (error) {
            console.error('Error fetching all news:', error);
            setLoadingAllNews(false);
        }
    };

    // Fetch events from Events API (all events, most recent first)
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoadingEvents(true);
                const response = await axios.get('/api/events/');
                const eventsData = Array.isArray(response.data)
                    ? response.data
                    : (response.data.results || []);

                const sorted = [...eventsData]
                    .filter(e => e.date)
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .slice(0, 3);

                setUpcomingEvents(sorted);
                setLoadingEvents(false);
            } catch (error) {
                console.error('Error fetching events:', error);
                setLoadingEvents(false);
                setUpcomingEvents([]);
            }
        };

        fetchEvents();
    }, []);

    // Change background image every 8 seconds (increased for better readability)
    useEffect(() => {
        const startInterval = () => {
            const id = setInterval(() => {
                handleNextImage();
            }, 8000);
            setIntervalId(id);
            return id;
        };

        const id = startInterval();
        return () => clearInterval(id);
    }, []);

    // Reset interval function
    const resetInterval = () => {
        if (intervalId) {
            clearInterval(intervalId);
        }
        const id = setInterval(() => {
            handleNextImage();
        }, 8000);
        setIntervalId(id);
    };

    // Handle manual navigation
    const handlePrevImage = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentImageIndex((prevIndex) => 
            prevIndex === 0 ? backgroundImages.length - 1 : prevIndex - 1
        );
        resetInterval(); // Reset the auto-change timer
        setTimeout(() => setIsTransitioning(false), 800);
    };

    const handleNextImage = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setCurrentImageIndex((prevIndex) => 
            (prevIndex + 1) % backgroundImages.length
        );
        setTimeout(() => setIsTransitioning(false), 800);
    };

    // Manual next with interval reset
    const handleManualNext = () => {
        handleNextImage();
        resetInterval();
    };

    // Handle gallery navigation
    const openGallery = (index) => {
        setSelectedImageIndex(index);
        setIsGalleryOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
    };

    const closeGallery = () => {
        setIsGalleryOpen(false);
        document.body.style.overflow = 'unset'; // Restore scrolling
    };

    const nextImage = () => {
        setSelectedImageIndex((prevIndex) => 
            (prevIndex + 1) % backgroundImages.length
        );
    };

    const prevImage = () => {
        setSelectedImageIndex((prevIndex) => 
            prevIndex === 0 ? backgroundImages.length - 1 : prevIndex - 1
        );
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (isGalleryOpen) {
                if (e.key === 'Escape') closeGallery();
                if (e.key === 'ArrowRight') nextImage();
                if (e.key === 'ArrowLeft') prevImage();
            }
            
            if (isNewsModalOpen && e.key === 'Escape') {
                setIsNewsModalOpen(false);
                setSelectedNews(null);
                document.body.style.overflow = 'unset';
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [isGalleryOpen, isNewsModalOpen]);

    // Track window width for responsive grid
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate grid columns based on count and screen width
    const getGridColumns = (count) => {
        if (windowWidth < 1024) {
            // CSS will handle 2 columns or 1 column via media queries
            return Math.min(count, 2);
        } else if (windowWidth < 1550) {
            // Max 3 columns for screens < 1550px
            return Math.min(count, 3);
        } else {
            // Max 4 columns for larger screens
            return Math.min(count, 4);
        }
    };

    // Intersection Observer for scroll animations
    useEffect(() => {
        const observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === newsRef.current && entry.isIntersecting) {
                    setNewsVisible(true);
                }
                if (entry.target === eventsRef.current && entry.isIntersecting) {
                    setEventsVisible(true);
                }
            });
        }, observerOptions);

        if (newsRef.current) observer.observe(newsRef.current);
        if (eventsRef.current) observer.observe(eventsRef.current);

        return () => {
            if (newsRef.current) observer.unobserve(newsRef.current);
            if (eventsRef.current) observer.unobserve(eventsRef.current);
        };
    }, []);

    // Handle scrolling to contact section when navigating from other pages
    useEffect(() => {
        if (location.hash === '#contact-us' || location.state?.scrollToContact) {
            // Small delay to ensure the page has rendered
            setTimeout(() => {
                const contactElement = document.getElementById('contact-us');
                if (contactElement) {
                    contactElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    // Clear the state after scrolling
                    if (location.state?.scrollToContact) {
                        navigate(location.pathname, { replace: true });
                    }
                }
            }, 100);
        }
    }, [location, navigate]);

    return (
        <div className="home">
            {/* Main Image Section with regular img tags */}
            <div className="home__image-section">
                {/* Background Images with crossfade effect */}
                {backgroundImages.map((imageData, index) => (
                    <img
                        key={index}
                        src={imageData.src}
                        alt={`Background ${index + 1}`}
                        className={`home__image ${
                            index === currentImageIndex ? 'home__image--active' : ''
                        }`}
                        onClick={() => openGallery(index)}
                    />
                ))}
                
                {/* Dark Overlay */}
                <div className="home__overlay"></div>

                {/* Navigation Arrows */}
                <button 
                    className="home__nav-arrow home__nav-arrow--left"
                    onClick={handlePrevImage}
                    disabled={isTransitioning}
                    title="Previous Image"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                    </svg>
                </button>

                <button 
                    className="home__nav-arrow home__nav-arrow--right"
                    onClick={handleManualNext}
                    disabled={isTransitioning}
                    title="Next Image"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                    </svg>
                </button>

                {/* Main Content with Dynamic Text */}
                <div className="home__content">
                    <div className={`home__text-content ${isTransitioning ? 'home__text-content--transitioning' : ''}`}>
                        <h1 className="home__title">
                            {backgroundImages[currentImageIndex].title}
                        </h1>
                        
                        <h2 className="home__subtitle">
                            {backgroundImages[currentImageIndex].subtitle}
                        </h2>
                        
                        <div className="home__intro">
                            <p className="home__description">
                                {backgroundImages[currentImageIndex].description}
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="home__actions">
                            <button
                                className="home__button home__button--primary"
                                onClick={() => navigate('/mission')}
                            >
                                See Mission & Bylaws →
                            </button>
                            <button 
                                className="home__button home__button--secondary"
                                onClick={() => navigate('/calendar')}
                            >
                                View Calendar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Links Section */}
            {/* <div className="home__quick-links">
                <div className="home__link-card">
                    <div className="home__link-icon">👥</div>
                    <h3>Executive Board</h3>
                    <p>Meet our leadership team</p>
                </div>
                <div className="home__link-card">
                    <div className="home__link-icon">📋</div>
                    <h3>Mission & Bylaws</h3>
                    <p>Our purpose and guidelines</p>
                </div>
                <div className="home__link-card">
                    <div className="home__link-icon">📅</div>
                    <h3>Upcoming Events</h3>
                    <p>Sales, meetings, and activities</p>
                </div>
                <div className="home__link-card">
                    <div className="home__link-icon">📸</div>
                    <h3>Past Events</h3>
                    <p>Photos from our activities</p>
                </div>
            </div> */}

            {/* Gallery Modal */}
            {isGalleryOpen && (
                <div className="gallery-modal" onClick={closeGallery}>
                    <div className="gallery-modal__content" onClick={(e) => e.stopPropagation()}>
                        {/* Close Button */}
                        <button 
                            className="gallery-modal__close"
                            onClick={closeGallery}
                        >
                            ×
                        </button>

                        {/* Navigation Arrows */}
                        <button 
                            className="gallery-modal__nav gallery-modal__nav--prev"
                            onClick={prevImage}
                        >
                            ‹
                        </button>
                        
                        <button 
                            className="gallery-modal__nav gallery-modal__nav--next"
                            onClick={nextImage}
                        >
                            ›
                        </button>

                        {/* Main Image */}
                        <img 
                            src={backgroundImages[selectedImageIndex].src}
                            alt={`Gallery image ${selectedImageIndex + 1}`}
                            className="gallery-modal__image"
                        />

                        {/* Image Counter */}
                        <div className="gallery-modal__counter">
                            {selectedImageIndex + 1} / {backgroundImages.length}
                        </div>

                        {/* Thumbnail Navigation */}
                        <div className="gallery-modal__thumbnails">
                            {backgroundImages.map((imageData, index) => (
                                <img
                                    key={index}
                                    src={imageData.src}
                                    alt={`Thumbnail ${index + 1}`}
                                    className={`gallery-modal__thumbnail ${
                                        index === selectedImageIndex ? 'gallery-modal__thumbnail--active' : ''
                                    }`}
                                    onClick={() => setSelectedImageIndex(index)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Latest News Section */}
            <section ref={newsRef} className={`home-latest-news ${newsVisible ? 'animate-in' : ''}`}>
                <div className="container">
                    <h2 className="section-title">Latest News</h2>
                    {loadingNews ? (
                        <div className="loading-message">Loading news...</div>
                    ) : latestNews.length > 0 ? (
                        <div 
                            className="news-grid"
                            style={{
                                gridTemplateColumns: `repeat(${getGridColumns(latestNews.length)}, 1fr)`
                            }}
                        >
                            {latestNews.map((newsItem, index) => (
                                <article 
                                    key={newsItem.id} 
                                    className={`news-card ${newsItem.is_featured ? 'news-card--featured' : ''} ${newsVisible ? 'animate-card' : ''}`}
                                    style={{ animationDelay: `${index * 0.2}s` }}
                                >
                                    <div className="news-card__image">
                                        <img 
                                            src={newsItem.image || "/media/bg-images/bg-image1.JPG"} 
                                            alt={newsItem.title}
                                            onError={(e) => {
                                                e.target.src = "/media/bg-images/bg-image1.JPG";
                                            }}
                                        />
                                        {newsItem.category && <div className="news-category">{newsItem.category}</div>}
                                    </div>
                                    
                                    <div className="news-card__content">
                                        <div className="news-meta">
                                            <span className="news-date">{newsItem.formatted_date || formatNewsDate(newsItem.date)}</span>
                                        </div>
                                        
                                        <h3 className="news-title">{newsItem.title}</h3>
                                        <p className="news-excerpt">{newsItem.excerpt}</p>
                                        
                                        <button 
                                            className="read-more-btn"
                                            onClick={() => {
                                                setSelectedNews(newsItem);
                                                setIsNewsModalOpen(true);
                                                document.body.style.overflow = 'hidden';
                                            }}
                                        >
                                            Read More →
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="no-news">
                            <h3>No news available</h3>
                            <p>Check back soon for updates.</p>
                        </div>
                    )}
                    {!loadingNews && latestNews.length > 0 && !showAllNews && (
                        <div className="view-all-news">
                            <button 
                                className="view-all-btn"
                                onClick={loadAllNews}
                                disabled={loadingAllNews}
                            >
                                {loadingAllNews ? 'Loading...' : 'See More News →'}
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Upcoming Events Section - Reusing Calendar Design */}
            <section ref={eventsRef} className={`home-upcoming-events ${eventsVisible ? 'animate-in' : ''}`}>
                <div className="container">
                    <h2 className="section-title">Upcoming Events</h2>
                    {loadingEvents ? (
                        <div className="loading-message">Loading events...</div>
                    ) : getUpcomingEventsForHome().length > 0 ? (
                        <div 
                            className="events-grid"
                            style={{
                                gridTemplateColumns: `repeat(${getGridColumns(getUpcomingEventsForHome().length)}, 1fr)`
                            }}
                        >
                            {getUpcomingEventsForHome().map((event, index) => {
                                const eventDate = formatEventDate(event.date);
                                return (
                                    <article 
                                        key={event.id} 
                                        className={`event-card ${eventsVisible ? 'animate-card' : ''}`}
                                        style={{ animationDelay: `${0.3 + (index * 0.15)}s` }}
                                    >
                                        <div className="event-card__header">
                                            <div className="event-date-badge">
                                                <span className="day">{eventDate.day}</span>
                                                <span className="month">{eventDate.month}</span>
                                            </div>
                                            <div className="event-type-badge">{getEventTypeLabel(event.type)}</div>
                                        </div>
                                        
                                        <div className="event-card__content">
                                            <div className="event-details">
                                                <div className="detail-item detail-item--time">
                                                    <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                        <polyline points="12 6 12 12 16 14"></polyline>
                                                    </svg>
                                                    <span className="detail-text">{event.time || 'All Day'}</span>
                                                </div>
                                                <div className="detail-item detail-item--location">
                                                    <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                        <circle cx="12" cy="10" r="3"></circle>
                                                    </svg>
                                                    <span 
                                                        className="detail-text event-location" 
                                                        title={event.location || 'TBD'}
                                                        onClick={() => openInGoogleMaps(event.location)}
                                                        style={{ cursor: event.location && event.location !== 'TBD' ? 'pointer' : 'default' }}
                                                    >
                                                        {event.location || 'TBD'}
                                                    </span>
                                                    {event.location && event.location !== 'TBD' && (
                                                        <button
                                                            className="copy-location-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                copyToClipboard(event.location, event.id);
                                                            }}
                                                            title={copiedLocationId === event.id ? 'Copied!' : 'Copy address'}
                                                        >
                                                            {copiedLocationId === event.id ? (
                                                                <svg className="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                                </svg>
                                                            ) : (
                                                                <svg className="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                                </svg>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="event-weekday">{eventDate.weekday}</div>
                                            <h3 className="event-title" style={{ margin: '0px'}}>{event.title}</h3>
                                            
                                            {event.description && (
                                                <p className="event-description">{event.description}</p>
                                            )}
                                        </div>
                                        
                                        <div className="event-card__footer">
                                            <div className="event-card__accent"></div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-events">
                            <h3>No upcoming events</h3>
                            <p>Please check back soon for new events.</p>
                        </div>
                    )}
                    {!loadingEvents && getUpcomingEventsForHome().length > 0 && (
                        <div className="view-all-events">
                            <button 
                                className="view-all-btn"
                                onClick={() => navigate('/all-events')}
                            >
                                View All Events →
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Google Calendar Section */}
            <section className="home-calendar-section">
                <div className="container">
                    <h2 className="section-title">Our Calendar</h2>
                    <div className="calendar-wrapper">
                        <iframe 
                            src="https://calendar.google.com/calendar/embed?height=700&wkst=1&ctz=America%2FNew_York&showPrint=0&src=MDc2MjkxMmQzOTE1ZTBkNDUwMTlkNDA3YjA3MDQxYTcwMWU1ODcyNDQwN2YyZmJmZmIyNDhlZTY4MThiNDZlM0Bncm91cC5jYWxlbmRhci5nb29nbGUuY29t&color=%23039be5" 
                            style={{ border: 'solid 1px #777' }} 
                            width="1200" 
                            height="700" 
                            frameBorder="0" 
                            scrolling="no"
                            title="Ladies Guild Calendar"
                        ></iframe>
                    </div>
                </div>
            </section>

            {/* Contact Us Section */}
            <section id="contact-us" ref={contactRef} className="home-contact-section">
                <div className="container">
                    <h2 className="section-title">Contact Us</h2>
                    <div className="contact-boxes">
                        <a 
                            href="mailto:ladiesguild@stsvartanantzchurch.com" 
                            className="contact-box"
                        >
                            <div className="contact-box__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                    <polyline points="22,6 12,13 2,6"></polyline>
                                </svg>
                            </div>
                            <h3 className="contact-box__title">Email Us</h3>
                            <p className="contact-box__info">ladiesguild@stsvartanantzchurch.com</p>
                        </a>

                        <a 
                            href="tel:+12019432950" 
                            className="contact-box"
                        >
                            <div className="contact-box__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                            </div>
                            <h3 className="contact-box__title">Call Us</h3>
                            <p className="contact-box__info">(201) 943-2950</p>
                        </a>

                        <a 
                            href="https://www.google.com/maps/search/?api=1&query=461+Bergen+Boulevard,+Ridgefield,+NJ+07657" 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contact-box"
                        >
                            <div className="contact-box__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                    <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                            </div>
                            <h3 className="contact-box__title">Visit Us</h3>
                            <p className="contact-box__info">461 Bergen Boulevard<br />Ridgefield, NJ 07657</p>
                        </a>
                    </div>
                </div>
            </section>

            {/* News Modal */}
            {isNewsModalOpen && selectedNews && (
                <div className="news-modal" onClick={() => {
                    setIsNewsModalOpen(false);
                    setSelectedNews(null);
                    document.body.style.overflow = 'unset';
                }}>
                    <div className="news-modal__content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="news-modal__close"
                            onClick={() => {
                                setIsNewsModalOpen(false);
                                setSelectedNews(null);
                                document.body.style.overflow = 'unset';
                            }}
                        >
                            ×
                        </button>
                        
                        {selectedNews.image && (
                            <div className="news-modal__image">
                                <img 
                                    src={selectedNews.image || "/media/bg-images/bg-image1.JPG"} 
                                    alt={selectedNews.title}
                                    onError={(e) => {
                                        e.target.src = "/media/bg-images/bg-image1.JPG";
                                    }}
                                />
                            </div>
                        )}
                        
                        <div className="news-modal__body">
                            <div className="news-modal__meta">
                                <span className="news-modal__category">{selectedNews.category}</span>
                                <span className="news-modal__date">
                                    {selectedNews.formatted_date || formatNewsDate(selectedNews.date)}
                                </span>
                                {selectedNews.author && (
                                    <span className="news-modal__author">By {selectedNews.author}</span>
                                )}
                            </div>
                            
                            <h2 className="news-modal__title">{selectedNews.title}</h2>
                            
                            <div className="news-modal__content-text">
                                {selectedNews.content.split('\n').map((paragraph, index) => (
                                    paragraph.trim() && (
                                        <p key={index}>{paragraph.trim()}</p>
                                    )
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Home;
